import { useEffect, useMemo, type ReactNode } from "react";

import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";

import { GroupedUsersGrid } from "./GroupedUsersGrid";
import { groupingDetailPath, type RetainedSearch } from "./userGrouping";

import type { IdpUsersGrouping_query$key } from "./__generated__/IdpUsersGrouping_query.graphql";
import type { IdpUsersGroupingQuery } from "./__generated__/IdpUsersGroupingQuery.graphql";
import type { IdpUsersGroupingRefetchQuery } from "./__generated__/IdpUsersGroupingRefetchQuery.graphql";
import type { BucketRow } from "./GroupedUsersGrid";

/** Cloud organizations fetched per page while loading them all. */
const CLOUD_ORG_PAGE = 100;

interface Props extends RetainedSearch {
  /** The group-by dropdown, for the grid toolbar. */
  toolbarActions?: ReactNode;
}

/**
 * Lists every SAML provider and cloud org, plus a "Local Users" section for
 * non-SSO users. Expanding one shows the users it provisioned. Cloud orgs are a
 * connection, so every page is loaded rather than a fixed first page.
 */
export function IdpUsersGrouping({
  toolbarActions,
  initialSearchTerm,
  onSearchTermChange,
}: Props) {
  const queryData = useLazyLoadQuery<IdpUsersGroupingQuery>(
    graphql`
      query IdpUsersGroupingQuery($first: Int) {
        ...IdpUsersGrouping_query @arguments(first: $first)
        samlProviders {
          displayName
          id
          idpNamespace
          name
        }
      }
    `,
    { first: CLOUD_ORG_PAGE },
  );

  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment<
    IdpUsersGroupingRefetchQuery,
    IdpUsersGrouping_query$key
  >(
    graphql`
      fragment IdpUsersGrouping_query on Query
      @argumentDefinitions(first: { type: "Int" }, after: { type: "String" })
      @refetchable(queryName: "IdpUsersGroupingRefetchQuery") {
        cloudOrganizations(first: $first, after: $after)
          @connection(key: "IdpUsersGrouping_cloudOrganizations") {
          edges {
            node {
              id
              name
              provider
              key
              idpNamespace
            }
          }
        }
      }
    `,
    queryData,
  );

  // Load every cloud org, not just the first page — the list groups them all,
  // so a fixed cap would silently omit providers.
  useEffect(() => {
    if (hasNext && !isLoadingNext) {
      loadNext(CLOUD_ORG_PAGE);
    }
  }, [hasNext, isLoadingNext, loadNext]);

  const buckets = useMemo<BucketRow[]>(() => {
    const samlRows: BucketRow[] = queryData.samlProviders.map((provider) => ({
      id: provider.id,
      label: provider.displayName || provider.name,
      to: groupingDetailPath({
        kind: "identity-provider",
        providerKind: "saml",
        name: provider.name,
        displayName: provider.displayName || provider.name,
      }),
      memberScope: {
        kind: "identity-provider",
        idpNamespace: provider.idpNamespace,
      },
      detail: "SAML",
    }));

    const cloudRows: BucketRow[] = (data.cloudOrganizations.edges ?? [])
      .map((edge) => edge.node)
      .filter((node): node is NonNullable<typeof node> => Boolean(node))
      .map((node) => ({
        id: node.id,
        label: node.name,
        to: groupingDetailPath({
          kind: "identity-provider",
          providerKind: "cloud-organization",
          provider: node.provider,
          key: node.key,
          displayName: node.name,
        }),
        memberScope: {
          kind: "identity-provider",
          idpNamespace: node.idpNamespace,
        },
        detail: node.provider,
      }));

    const localRow: BucketRow = {
      id: "__local__",
      label: "Local Users",
      to: null,
      memberScope: { kind: "local" },
      detail: "No identity provider",
    };

    return [...samlRows, ...cloudRows, localRow];
  }, [queryData.samlProviders, data.cloudOrganizations.edges]);

  // Search buckets by the user's `identityLinks`: matched users fall under the
  // provider(s) they're linked to, and unlinked ones under Local Users.
  return (
    <GroupedUsersGrid
      buckets={buckets}
      initialSearchTerm={initialSearchTerm}
      loading={isLoadingNext}
      onSearchTermChange={onSearchTermChange}
      searchKind="identity-provider"
      tableId="grouped-users-idps"
      toolbarActions={toolbarActions}
      unit="provider"
    />
  );
}
